import { useState, useEffect, useCallback } from 'react';

const STORAGE_PREFIX = 'fbc_pref_';

/**
 * Persists a React state value to localStorage so it survives reloads.
 * Drop-in replacement for useState when you want layout/UX preferences
 * (sidebar collapse, active section, last filters, form drafts, etc.)
 * to carry across sessions on the same device.
 *
 * @param {string} key     Unique preference key (prefixed automatically).
 * @param {*}      initial Default value if nothing is stored yet.
 * @param {object} options
 *   - validate(value): optional guard returning true if stored value is still valid.
 *     When it returns false, the stored value is discarded and `initial` is used.
 *
 * @returns [value, setValue, reset]
 */
export function useLayoutPref(key, initial, options = {}) {
  const storageKey = STORAGE_PREFIX + key;
  const { validate } = options;

  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return initial;
      const parsed = JSON.parse(raw);
      if (validate && !validate(parsed)) return initial;
      return parsed;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Storage may be full or disabled — silently ignore.
    }
  }, [storageKey, value]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    setValue(initial);
  }, [storageKey, initial]);

  return [value, setValue, reset];
}
